export type Priority = 'High' | 'Medium' | 'Low';
export type TaskStatus = 'To Do' | 'In Progress' | 'In Review' | 'Done';
export type ProjectStatus = 'Active' | 'On Hold' | 'Completed';

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  avatarInitials: string;
  avatarColor: string;
  activeTasks: number;
  completedThisWeek: number;
}

export interface SubTask {
  id: string;
  title: string;
  completed: boolean;
}

export interface Comment {
  id: string;
  authorId: string;
  authorName: string;
  authorInitials: string;
  authorColor: string;
  content: string;
  createdAt: string;
}

export interface Attachment {
  id: string;
  name: string;
  size: string;
  type: string;
  uploadedAt: string;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: Priority;
  assigneeId: string;
  projectId: string;
  dueDate: string;
  tags: string[];
  subTasks: SubTask[];
  comments: Comment[];
  attachments: Attachment[];
  createdById: string;
  createdAt: string;
  commentCount: number;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  status: ProjectStatus;
  startDate: string;
  endDate: string;
  teamMemberIds: string[];
  tasks: Task[];
  progress: number;
}

export interface ActivityItem {
  id: string;
  userId: string;
  userName: string;
  userInitials: string;
  userColor: string;
  action: string;
  target: string;
  projectName: string;
  timestamp: string;
}

export const TEAM_MEMBERS: TeamMember[] = [
  {
    id: 'user-001',
    name: 'Alex Morgan',
    email: 'alex.morgan@taskflowpro.io',
    role: 'Project Manager',
    avatarInitials: 'AM',
    avatarColor: '#7c3aed',
    activeTasks: 5,
    completedThisWeek: 8,
  },
  {
    id: 'user-002',
    name: 'Sarah Chen',
    email: 'sarah.chen@taskflowpro.io',
    role: 'Frontend Developer',
    avatarInitials: 'SC',
    avatarColor: '#0891b2',
    activeTasks: 7,
    completedThisWeek: 5,
  },
  {
    id: 'user-003',
    name: 'Marcus Reid',
    email: 'marcus.reid@taskflowpro.io',
    role: 'Backend Developer',
    avatarInitials: 'MR',
    avatarColor: '#059669',
    activeTasks: 6,
    completedThisWeek: 6,
  },
  {
    id: 'user-004',
    name: 'Priya Nair',
    email: 'priya.nair@taskflowpro.io',
    role: 'UX Designer',
    avatarInitials: 'PN',
    avatarColor: '#dc2626',
    activeTasks: 4,
    completedThisWeek: 7,
  },
];

export const PROJECTS: Project[] = [
  {
    id: 'proj-001',
    name: 'Website Redesign',
    description:
      'Complete overhaul of the company marketing site with new brand identity, improved conversion funnels, and mobile-first responsive design.',
    status: 'Active',
    startDate: '2026-04-01',
    endDate: '2026-06-30',
    teamMemberIds: ['user-001', 'user-002', 'user-004'],
    progress: 62,
    tasks: [
      {
        id: 'task-001',
        title: 'Audit current site performance and UX issues',
        description:
          'Run Lighthouse audits, heatmap analysis, and document all identified UX friction points on the current website.',
        status: 'Done',
        priority: 'High',
        assigneeId: 'user-004',
        projectId: 'proj-001',
        dueDate: '2026-04-15',
        tags: ['audit', 'ux', 'performance'],
        commentCount: 4,
        subTasks: [
          { id: 'st-001', title: 'Run Lighthouse on all key pages', completed: true },
          { id: 'st-002', title: 'Export heatmap data from Hotjar', completed: true },
          { id: 'st-003', title: 'Document findings in Notion', completed: true },
        ],
        comments: [
          {
            id: 'c-001',
            authorId: 'user-002',
            authorName: 'Sarah Chen',
            authorInitials: 'SC',
            authorColor: '#0891b2',
            content: 'Lighthouse scores are pretty bad on mobile — averaging 42. We should prioritize image optimization.',
            createdAt: '2026-04-10T10:30:00Z',
          },
          {
            id: 'c-002',
            authorId: 'user-004',
            authorName: 'Priya Nair',
            authorInitials: 'PN',
            authorColor: '#dc2626',
            content: 'Agreed. Also the checkout flow has a 68% drop-off — that needs immediate attention.',
            createdAt: '2026-04-10T11:15:00Z',
          },
        ],
        attachments: [
          { id: 'att-001', name: 'lighthouse-report.pdf', size: '2.4 MB', type: 'pdf', uploadedAt: '2026-04-10' },
        ],
        createdById: 'user-001',
        createdAt: '2026-04-01T09:00:00Z',
      },
      {
        id: 'task-002',
        title: 'Design new homepage wireframes',
        description:
          'Create low and high-fidelity wireframes for the new homepage layout. Focus on hero section, social proof, and CTA placement.',
        status: 'Done',
        priority: 'High',
        assigneeId: 'user-004',
        projectId: 'proj-001',
        dueDate: '2026-04-28',
        tags: ['design', 'wireframe'],
        commentCount: 6,
        subTasks: [
          { id: 'st-004', title: 'Sketch low-fi concepts (3 variants)', completed: true },
          { id: 'st-005', title: 'Present to stakeholders', completed: true },
          { id: 'st-006', title: 'Finalize high-fi in Figma', completed: true },
        ],
        comments: [],
        attachments: [
          { id: 'att-002', name: 'homepage-wireframes-v3.fig', size: '8.1 MB', type: 'figma', uploadedAt: '2026-04-25' },
        ],
        createdById: 'user-001',
        createdAt: '2026-04-02T09:00:00Z',
      },
      {
        id: 'task-003',
        title: 'Implement responsive navigation component',
        description:
          'Build the new top navigation bar with mobile hamburger menu, dropdown mega-menu, and sticky scroll behavior.',
        status: 'In Review',
        priority: 'High',
        assigneeId: 'user-002',
        projectId: 'proj-001',
        dueDate: '2026-05-30',
        tags: ['frontend', 'navigation', 'responsive'],
        commentCount: 3,
        subTasks: [
          { id: 'st-007', title: 'Desktop nav layout', completed: true },
          { id: 'st-008', title: 'Mobile hamburger menu', completed: true },
          { id: 'st-009', title: 'Accessibility audit (WCAG 2.1)', completed: false },
        ],
        comments: [
          {
            id: 'c-003',
            authorId: 'user-001',
            authorName: 'Alex Morgan',
            authorInitials: 'AM',
            authorColor: '#7c3aed',
            content: 'Looks great on desktop. Can you check the hamburger menu animation on iOS Safari?',
            createdAt: '2026-05-24T14:00:00Z',
          },
        ],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-05-01T09:00:00Z',
      },
      {
        id: 'task-004',
        title: 'Build product feature section with animations',
        description:
          'Implement the feature showcase section with scroll-triggered animations, icon illustrations, and tabbed content.',
        status: 'In Progress',
        priority: 'Medium',
        assigneeId: 'user-002',
        projectId: 'proj-001',
        dueDate: '2026-06-05',
        tags: ['frontend', 'animation'],
        commentCount: 1,
        subTasks: [
          { id: 'st-010', title: 'Static layout implementation', completed: true },
          { id: 'st-011', title: 'Scroll-triggered animations', completed: false },
          { id: 'st-012', title: 'Tab switching logic', completed: false },
        ],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-05-05T09:00:00Z',
      },
      {
        id: 'task-005',
        title: 'SEO meta tags and structured data',
        description:
          'Implement Open Graph tags, JSON-LD structured data, and sitemap for all key pages.',
        status: 'To Do',
        priority: 'Medium',
        assigneeId: 'user-001',
        projectId: 'proj-001',
        dueDate: '2026-06-15',
        tags: ['seo', 'meta'],
        commentCount: 0,
        subTasks: [],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-05-10T09:00:00Z',
      },
      {
        id: 'task-006',
        title: 'Performance optimization — Core Web Vitals',
        description:
          'Optimize LCP, FID, and CLS scores to achieve green scores across all pages. Focus on image lazy loading and JS bundle splitting.',
        status: 'To Do',
        priority: 'Low',
        assigneeId: 'user-002',
        projectId: 'proj-001',
        dueDate: '2026-06-25',
        tags: ['performance', 'optimization'],
        commentCount: 0,
        subTasks: [],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-05-10T09:00:00Z',
      },
    ],
  },
  {
    id: 'proj-002',
    name: 'Mobile App Launch',
    description:
      'Native iOS and Android app for the core product. Targeting App Store and Google Play release with feature parity to web v2.',
    status: 'Active',
    startDate: '2026-03-15',
    endDate: '2026-07-31',
    teamMemberIds: ['user-001', 'user-002', 'user-003', 'user-004'],
    progress: 44,
    tasks: [
      {
        id: 'task-007',
        title: 'Define app architecture and tech stack',
        description: 'Evaluate React Native vs Flutter vs native. Document final decision with benchmarks.',
        status: 'Done',
        priority: 'High',
        assigneeId: 'user-003',
        projectId: 'proj-002',
        dueDate: '2026-03-25',
        tags: ['architecture', 'planning'],
        commentCount: 7,
        subTasks: [
          { id: 'st-013', title: 'Research React Native performance', completed: true },
          { id: 'st-014', title: 'Flutter prototype (2 screens)', completed: true },
          { id: 'st-015', title: 'Present findings to team', completed: true },
        ],
        comments: [],
        attachments: [
          { id: 'att-003', name: 'tech-stack-decision.pdf', size: '1.2 MB', type: 'pdf', uploadedAt: '2026-03-24' },
        ],
        createdById: 'user-001',
        createdAt: '2026-03-15T09:00:00Z',
      },
      {
        id: 'task-008',
        title: 'Design onboarding flow screens',
        description: 'Create 6-screen onboarding flow with permission requests, profile setup, and feature highlights.',
        status: 'Done',
        priority: 'High',
        assigneeId: 'user-004',
        projectId: 'proj-002',
        dueDate: '2026-04-10',
        tags: ['design', 'onboarding'],
        commentCount: 5,
        subTasks: [
          { id: 'st-016', title: 'User flow diagram', completed: true },
          { id: 'st-017', title: 'Screen designs in Figma', completed: true },
        ],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-03-16T09:00:00Z',
      },
      {
        id: 'task-009',
        title: 'Implement authentication screens',
        description: 'Build login, signup, forgot password, and biometric auth screens.',
        status: 'In Progress',
        priority: 'High',
        assigneeId: 'user-003',
        projectId: 'proj-002',
        dueDate: '2026-05-28',
        tags: ['auth', 'mobile'],
        commentCount: 2,
        subTasks: [
          { id: 'st-018', title: 'Login screen', completed: true },
          { id: 'st-019', title: 'Signup with validation', completed: true },
          { id: 'st-020', title: 'Biometric auth integration', completed: false },
        ],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-04-15T09:00:00Z',
      },
      {
        id: 'task-010',
        title: 'Push notification infrastructure',
        description: 'Set up FCM/APNs integration, notification templates, and preference management.',
        status: 'In Progress',
        priority: 'Medium',
        assigneeId: 'user-003',
        projectId: 'proj-002',
        dueDate: '2026-06-10',
        tags: ['backend', 'notifications'],
        commentCount: 0,
        subTasks: [
          { id: 'st-021', title: 'FCM setup', completed: true },
          { id: 'st-022', title: 'APNs certificate configuration', completed: false },
          { id: 'st-023', title: 'Notification preference API', completed: false },
        ],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-04-20T09:00:00Z',
      },
      {
        id: 'task-011',
        title: 'App Store listing and screenshots',
        description: 'Prepare App Store and Play Store listings with screenshots, preview video, and copy.',
        status: 'To Do',
        priority: 'Medium',
        assigneeId: 'user-004',
        projectId: 'proj-002',
        dueDate: '2026-07-15',
        tags: ['marketing', 'appstore'],
        commentCount: 0,
        subTasks: [],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-05-01T09:00:00Z',
      },
      {
        id: 'task-012',
        title: 'Beta testing program — 50 users',
        description: 'Recruit beta testers, distribute TestFlight/Play Console builds, collect structured feedback.',
        status: 'To Do',
        priority: 'High',
        assigneeId: 'user-001',
        projectId: 'proj-002',
        dueDate: '2026-07-01',
        tags: ['testing', 'beta'],
        commentCount: 0,
        subTasks: [],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-05-01T09:00:00Z',
      },
    ],
  },
  {
    id: 'proj-003',
    name: 'Marketing Campaign',
    description:
      'Q2 demand generation campaign targeting mid-market SaaS companies. Includes paid ads, content series, and partner webinars.',
    status: 'On Hold',
    startDate: '2026-05-01',
    endDate: '2026-06-30',
    teamMemberIds: ['user-001', 'user-004'],
    progress: 28,
    tasks: [
      {
        id: 'task-013',
        title: 'Define ICP and campaign messaging',
        description: 'Document the ideal customer profile and create messaging framework for all campaign channels.',
        status: 'Done',
        priority: 'High',
        assigneeId: 'user-001',
        projectId: 'proj-003',
        dueDate: '2026-05-10',
        tags: ['strategy', 'messaging'],
        commentCount: 3,
        subTasks: [
          { id: 'st-024', title: 'ICP document', completed: true },
          { id: 'st-025', title: 'Messaging matrix', completed: true },
        ],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-05-01T09:00:00Z',
      },
      {
        id: 'task-014',
        title: 'Create 6-part content series',
        description: 'Write and design 6 long-form articles on project management best practices for the blog.',
        status: 'In Progress',
        priority: 'Medium',
        assigneeId: 'user-004',
        projectId: 'proj-003',
        dueDate: '2026-05-20',
        tags: ['content', 'blog'],
        commentCount: 1,
        subTasks: [
          { id: 'st-026', title: 'Article 1 — draft', completed: true },
          { id: 'st-027', title: 'Article 2 — draft', completed: false },
          { id: 'st-028', title: 'Articles 3-6 outline', completed: false },
        ],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-05-02T09:00:00Z',
      },
      {
        id: 'task-015',
        title: 'Set up paid ad campaigns (Google + LinkedIn)',
        description: 'Configure Google Search and LinkedIn Lead Gen campaigns with proper tracking and conversion goals.',
        status: 'To Do',
        priority: 'High',
        assigneeId: 'user-001',
        projectId: 'proj-003',
        dueDate: '2026-05-15',
        tags: ['paid-ads', 'google', 'linkedin'],
        commentCount: 0,
        subTasks: [],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-05-03T09:00:00Z',
      },
      {
        id: 'task-016',
        title: 'Partner webinar — 3 sessions',
        description: 'Coordinate with 3 partner companies for co-hosted webinars. Handle scheduling, promotion, and follow-up.',
        status: 'To Do',
        priority: 'Low',
        assigneeId: 'user-004',
        projectId: 'proj-003',
        dueDate: '2026-06-20',
        tags: ['webinar', 'partnerships'],
        commentCount: 0,
        subTasks: [],
        comments: [],
        attachments: [],
        createdById: 'user-001',
        createdAt: '2026-05-05T09:00:00Z',
      },
    ],
  },
];

export const RECENT_ACTIVITY: ActivityItem[] = [
  {
    id: 'act-001',
    userId: 'user-002',
    userName: 'Sarah Chen',
    userInitials: 'SC',
    userColor: '#0891b2',
    action: 'completed',
    target: 'Design new homepage wireframes',
    projectName: 'Website Redesign',
    timestamp: '2026-05-27T09:45:00Z',
  },
  {
    id: 'act-002',
    userId: 'user-003',
    userName: 'Marcus Reid',
    userInitials: 'MR',
    userColor: '#059669',
    action: 'moved to In Progress',
    target: 'Implement authentication screens',
    projectName: 'Mobile App Launch',
    timestamp: '2026-05-27T09:12:00Z',
  },
  {
    id: 'act-003',
    userId: 'user-001',
    userName: 'Alex Morgan',
    userInitials: 'AM',
    userColor: '#7c3aed',
    action: 'created task',
    target: 'Beta testing program — 50 users',
    projectName: 'Mobile App Launch',
    timestamp: '2026-05-27T08:55:00Z',
  },
  {
    id: 'act-004',
    userId: 'user-004',
    userName: 'Priya Nair',
    userInitials: 'PN',
    userColor: '#dc2626',
    action: 'commented on',
    target: 'Implement responsive navigation component',
    projectName: 'Website Redesign',
    timestamp: '2026-05-26T17:30:00Z',
  },
  {
    id: 'act-005',
    userId: 'user-003',
    userName: 'Marcus Reid',
    userInitials: 'MR',
    userColor: '#059669',
    action: 'completed',
    target: 'Define app architecture and tech stack',
    projectName: 'Mobile App Launch',
    timestamp: '2026-05-26T16:00:00Z',
  },
  {
    id: 'act-006',
    userId: 'user-001',
    userName: 'Alex Morgan',
    userInitials: 'AM',
    userColor: '#7c3aed',
    action: 'updated priority on',
    target: 'Set up paid ad campaigns',
    projectName: 'Marketing Campaign',
    timestamp: '2026-05-26T14:20:00Z',
  },
];

export const LOGGED_IN_USER = TEAM_MEMBERS[0]; // Alex Morgan